import { AnalyticsUtilsAbstract, AppParams, IUacBackendInfo, UserInfo } from '@traceair/browser-tracking-lib';
import { useAppStore } from '../../store/appStore';
import TokenService from '../../api_services/TokenService/TokenService';
import { Customer, MissionCustomer, Site } from '../../types';

const { enabled, backendUrl, environment, location } = Tracking;
const appVersion = process.env.REACT_APP_VERSION as string;

type TrackParams = [eventMsg?: string, params?: Record<string, unknown>];

class PilotAnalyticsUtils extends AnalyticsUtilsAbstract {
  private userInfo?: UserInfo;

  private isUserInfoInitialized = false;

  private eventsBeforeUserInit: TrackParams[] = [];

  constructor() {
    super();

    if (!enabled) {
      return;
    }

    this.setSessionId();
  }

  updateUserInfo = () => {
    const userMissions = useAppStore.getState().missions;
    const userCustomers: string[] = (userMissions ?? []).map((m: MissionCustomer) => m.name);
    const currentUser = TokenService.getUser();

    if (currentUser) {
      this.userInfo = {
        userId: currentUser.email || '',
        email: currentUser.email,
        name: currentUser.name,
        title: currentUser.title,
        company: currentUser.company ? currentUser.company.name : '',
        companyId: currentUser.company ? currentUser.company.id : '',
        department: currentUser.department || '',
        companyType: currentUser.company ? currentUser.company.type : '',
        titleDescription: currentUser.titleDescription || '',
        customers: userCustomers,
        phone: currentUser.phone || '',
        lang: currentUser.lang,
        lastname: currentUser.lastname,
        createdTime: strDateToTimestamp(currentUser.createdAt),
        updatedTime: strDateToTimestamp(currentUser.updatedAt),
        location,
      };

      if (this.isEnabled()) {
        this.setUserIdCookie(currentUser.email);
        this.setLang(currentUser.lang);

        this.isUserInfoInitialized = true;
        this.sendPreUserInitEvents();
      }
    }
  };

  getAppParams() {
    return {
      componentVersion: appVersion,
      component: 'Pilot App',
      componentData: {},
      subsystem: 'BO',
      env: environment,
    } as AppParams;
  }

  getUserInfo(): UserInfo | undefined {
    return this.userInfo;
  }

  async getBackendInfo(): Promise<IUacBackendInfo> {
    const { customer, site } = this.getSiteParams();

    if (site && customer) {
      const jwt = TokenService.findTokenForContext({ customer, site })?.token as string;

      if (!jwt) {
        return {};
      }

      const url = `${backendUrl}/api/customers/${customer}/sites/${site}/store-action`;

      return { url, jwt };
    } else {
      return {
        url: `${backendUrl}/api/store-action`,
      };
    }
  }

  isEnabled() {
    return enabled;
  }

  trackEventsOnlyWithUserInfo(eventMsg: TrackParams[0], params: TrackParams[1]) {
    if (this.isUserInfoInitialized) {
      this.track(eventMsg, params);
    } else {
      this.eventsBeforeUserInit.push([eventMsg, params]);
    }
  }

  sendPreUserInitEvents() {
    for (const eventData of this.eventsBeforeUserInit) {
      const [...args] = eventData;

      this.track(...args);
    }
  }
}

function strDateToTimestamp(date?: string): number | undefined {
  return date ? new Date(date).getTime() : undefined;
}

const analytics = new PilotAnalyticsUtils();

function refreshUserInfo() {
  analytics.updateUserInfo();
}

function trackAction(
  eventMsg = 'incorrect event',
  context: { customer: Customer; site: Site } | null = null,
  params: TrackParams[1] = {},
) {
  if (context) {
    const { customer, site } = context;

    params.Company = customer.id;
    params.ConstructionSite = site.id;
    params.ConstructionSiteName = site.name;
  }

  analytics.trackEventsOnlyWithUserInfo(`(Pilot App) ${eventMsg}`, params);
}

export { trackAction, refreshUserInfo };
